<script>
export default {
  name: 'ExecutionDetail',

  props: {
    execution: {
      type: Object,
      required: true,
    },
  },

  emits: ['close'],

  computed: {
    hasActions() {
      return this.execution.actions && this.execution.actions.length > 0;
    },

    conditionsEvaluated() {
      return this.execution.execution_context?.conditions_evaluated || [];
    },
  },

  methods: {
    statusIcon(status) {
      return (
        {
          success: 'i-lucide-check-circle',
          failed: 'i-lucide-x-circle',
          skipped: 'i-lucide-minus-circle',
          partial: 'i-lucide-alert-circle',
        }[status] || 'i-lucide-circle'
      );
    },

    actionStatusIcon(status) {
      return (
        {
          success: 'i-lucide-check',
          failed: 'i-lucide-x',
          skipped: 'i-lucide-minus',
        }[status] || 'i-lucide-circle'
      );
    },

    statusLabel(status) {
      return (
        {
          success: 'Sucesso',
          failed: 'Falha',
          skipped: 'Ignorado',
          partial: 'Parcial',
        }[status] || status
      );
    },

    formatEvent(event) {
      const labels = {
        deal_created: 'Negócio criado',
        deal_updated: 'Negócio atualizado',
        deal_stage_changed: 'Stage alterado',
        deal_won: 'Negócio ganho',
        deal_lost: 'Negócio perdido',
        deal_assignee_changed: 'Responsável alterado',
      };
      return labels[event] || event;
    },

    formatActionName(name) {
      const labels = {
        assign_agent: 'Atribuir responsável',
        change_stage: 'Mudar stage',
        change_status: 'Mudar status',
        change_priority: 'Mudar prioridade',
        mark_as_lost: 'Marcar como perdido',
        add_note: 'Adicionar nota',
        create_activity: 'Criar atividade',
        send_webhook_event: 'Enviar webhook',
      };
      return labels[name] || name;
    },

    formatTime(isoString) {
      if (!isoString) return '';
      return new Date(isoString).toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    },

    showOutput(action) {
      return (
        action.status === 'success' &&
        action.output_data &&
        Object.keys(action.output_data).length > 0
      );
    },
  },
};
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template -->
  <div class="execution-detail-overlay">
    <div class="detail-panel">
      <div class="detail-header">
        <div class="detail-title">
          <span class="detail-badge" :class="`status-${execution.status}`">
            <span :class="statusIcon(execution.status)" />
            {{ statusLabel(execution.status) }}
          </span>
          <span class="detail-time">{{
            formatTime(execution.executed_at)
          }}</span>
        </div>
        <woot-button
          variant="ghost"
          color="slate"
          icon="i-lucide-x"
          size="sm"
          @click="$emit('close')"
        />
      </div>

      <div class="detail-body">
        <div class="detail-meta-section">
          <div class="meta-row">
            <span class="meta-label">Evento</span>
            <span class="meta-value">{{
              formatEvent(execution.trigger_event)
            }}</span>
          </div>
          <div v-if="execution.trigger_resource_id" class="meta-row">
            <span class="meta-label">Deal ID</span>
            <span class="meta-value">#{{ execution.trigger_resource_id }}</span>
          </div>
          <div v-if="execution.duration_ms" class="meta-row">
            <span class="meta-label">Duração</span>
            <span class="meta-value">{{ execution.duration_ms }}ms</span>
          </div>
          <div v-if="execution.conditions_matched !== null" class="meta-row">
            <span class="meta-label">Condições</span>
            <span
              class="meta-value"
              :class="execution.conditions_matched ? 'text-green' : 'text-red'"
            >
              {{ execution.conditions_matched ? 'Atendidas' : 'Não atendidas' }}
            </span>
          </div>
        </div>

        <div v-if="execution.error_message" class="error-section">
          <span class="error-label">Erro</span>
          <code class="error-message">{{ execution.error_message }}</code>
        </div>

        <div v-if="hasActions" class="actions-section">
          <h4 class="section-title">Ações executadas</h4>
          <div class="action-timeline">
            <div
              v-for="action in execution.actions"
              :key="action.id"
              class="action-item"
              :class="`action-status-${action.status}`"
            >
              <div class="action-connector" />
              <div class="action-dot" :class="`dot-${action.status}`">
                <span :class="actionStatusIcon(action.status)" />
              </div>
              <div class="action-content">
                <div class="action-header">
                  <span class="action-name">{{
                    formatActionName(action.action_name)
                  }}</span>
                  <span v-if="action.duration_ms" class="action-duration">
                    {{ action.duration_ms }}ms
                  </span>
                </div>
                <div v-if="action.error_message" class="action-error">
                  {{ action.error_message }}
                </div>
                <div v-if="showOutput(action)" class="action-output">
                  <code>{{ JSON.stringify(action.output_data, null, 2) }}</code>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-else-if="execution.status === 'skipped'" class="skipped-section">
          <span class="i-lucide-info skipped-icon" />
          <p class="skipped-message">
            As condições da regra não foram atendidas para este evento.
          </p>
          <div v-if="conditionsEvaluated.length" class="conditions-list">
            <h4 class="section-title">Condições avaliadas</h4>
            <div
              v-for="(cond, idx) in conditionsEvaluated"
              :key="idx"
              class="condition-item"
              :class="cond.matched ? 'cond-matched' : 'cond-not-matched'"
            >
              <span :class="cond.matched ? 'i-lucide-check' : 'i-lucide-x'" />
              <span class="cond-key">{{ cond.attribute_key }}</span>
              <span class="cond-op">{{ cond.filter_operator }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.execution-detail-overlay {
  position: fixed;
  inset: 0;
  z-index: 1100;
  display: flex;
  justify-content: flex-end;
  background: rgba(0, 0, 0, 0.15);
}

.detail-panel {
  width: 480px;
  background: white;
  display: flex;
  flex-direction: column;
  box-shadow: -4px 0 24px rgba(0, 0, 0, 0.2);
  overflow: hidden;
}

.detail-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
}

.detail-title {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.detail-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 20px;
}

.status-success {
  background: #dcfce7;
  color: #16a34a;
}
.status-failed {
  background: #fee2e2;
  color: #dc2626;
}
.status-skipped {
  background: #f3f4f6;
  color: #6b7280;
}
.status-partial {
  background: #fef9c3;
  color: #ca8a04;
}

.detail-time {
  font-size: 11px;
  color: #9ca3af;
  padding-left: 2px;
}

.detail-body {
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.detail-meta-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  background: #f9fafb;
  border-radius: 8px;
}

.meta-row {
  display: flex;
  justify-content: space-between;
  font-size: 13px;
}

.meta-label {
  color: #6b7280;
}
.meta-value {
  color: #111827;
  font-weight: 500;
}
.text-green {
  color: #16a34a;
}
.text-red {
  color: #dc2626;
}

.error-section {
  background: #fff1f2;
  border: 1px solid #fecdd3;
  border-radius: 8px;
  padding: 12px;
}

.error-label {
  font-size: 11px;
  font-weight: 600;
  color: #dc2626;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  display: block;
  margin-bottom: 6px;
}

.error-message {
  font-size: 12px;
  color: #dc2626;
  font-family: monospace;
  white-space: pre-wrap;
  word-break: break-all;
}

.section-title {
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 12px;
}

.action-timeline {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.action-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  position: relative;
  padding-bottom: 16px;
}

.action-item:last-child {
  padding-bottom: 0;
}

.action-item:last-child .action-connector {
  display: none;
}

.action-connector {
  position: absolute;
  left: 15px;
  top: 28px;
  bottom: 0;
  width: 2px;
  background: #e5e7eb;
}

.action-dot {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 14px;
  position: relative;
  z-index: 1;
}

.dot-success {
  background: #dcfce7;
  color: #16a34a;
}
.dot-failed {
  background: #fee2e2;
  color: #dc2626;
}
.dot-skipped {
  background: #f3f4f6;
  color: #9ca3af;
}

.action-content {
  flex: 1;
  padding-top: 4px;
}

.action-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
}

.action-name {
  font-size: 13px;
  font-weight: 500;
  color: #374151;
}

.action-duration {
  font-size: 11px;
  color: #9ca3af;
}

.action-error {
  font-size: 12px;
  color: #dc2626;
  background: #fff1f2;
  padding: 6px 8px;
  border-radius: 4px;
  margin-top: 4px;
}

.action-output {
  margin-top: 6px;
}

.action-output code {
  display: block;
  font-size: 11px;
  color: #374151;
  background: #f3f4f6;
  padding: 8px;
  border-radius: 4px;
  white-space: pre-wrap;
  word-break: break-all;
  max-height: 120px;
  overflow-y: auto;
}

.skipped-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 20px;
  background: #f9fafb;
  border-radius: 8px;
  text-align: center;
}

.skipped-icon {
  font-size: 32px;
  color: #9ca3af;
}

.skipped-message {
  font-size: 13px;
  color: #6b7280;
  margin: 0;
}

.conditions-list {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.condition-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 12px;
}

.cond-matched {
  background: #dcfce7;
  color: #16a34a;
}
.cond-not-matched {
  background: #fee2e2;
  color: #dc2626;
}

.cond-key {
  font-weight: 500;
}
.cond-op {
  color: currentColor;
  opacity: 0.7;
}
</style>
